import { AgentQueuesDetail } from '@nice-ccf/common-sdk';
import { AgentSessionManager, ApiUriConstants, HttpClient, HttpPollingRequestInit,
  HttpUtilService, Logger, UrlUtilsService } from '@nice-ccf/core-sdk';
import { wrap } from 'comlink';
import { CXoneClient } from '../cxone-client';
import { AgentQueuesDetailWorker } from '../worker/cxone-agent-queues-detail-worker';

/**
 * Agent Queues Detail Provider Class
 */
export class CXoneAgentQueuesDetailProvider {
  private logger: Logger = new Logger('SDK', 'CXoneAgentQueuesDetailProvider');
  private agentSession = AgentSessionManager.instance;
  private baseUri = '';
  private utilService: HttpUtilService = new HttpUtilService();
  private cxoneClient = <CXoneClient>{};
  private urlUtilService: UrlUtilsService = new UrlUtilsService();

  public pollingWorker: any;
  /**
   * Used to set the directory base instance to access the subject from the base class
   * @example -
   */
  public setACDSdkBaseInstance(cxoneClient: CXoneClient) {
    this.cxoneClient = cxoneClient;
  }

  /**
   * Used to initiate the polling for agent queues detail data
   * @example -
   * ```
   * const agentQueuesDetailProvider = new CXoneAgentQueuesDetailProvider();
   * this.agentQueuesDetailProvider.agentQueuesDetailsPolling();
   * ```
   */
  public agentQueuesDetailsPolling(agentId: string) {
    if (this.pollingWorker) {
      this.logger.info(
        'agentQueuesDetailsPolling',
        'agentQueuesDetailsPolling is already started'
      );
      return;
    }
    this.logger.info(
      'agentQueuesDetailsPolling',
      'agentQueuesDetailsPolling in CXoneAgentQueuesDetailProvider'
    );
    this.baseUri = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    const requestParams = {
      fields: '',
      updatedSince: new Date(0).toISOString(),
    };
    if (this.baseUri && authToken) {
      const { startAgentQueuesDetailPolling } =
        this.initAgentQueuesDetailWorker();
      const queueUri = ApiUriConstants.AGENT_QUEUE_DETAIL_URI.replace(
        '{agentId}',
        agentId
      );
      const url =
        this.baseUri +
        this.urlUtilService.appendQueryString(queueUri, requestParams);

      const reqInit: HttpPollingRequestInit = {
        headers: this.utilService.initPollingHeader(
          authToken,
          'application/json'
        ),
        method: 'GET',
      };
      HttpClient.getWithPolling(
        {url:url,request:reqInit},
        this.handleAgentQueuesDetailResponse.bind(this),
        startAgentQueuesDetailPolling
      );
    }
  }

  /**
   * Callback method which will passed on to the worker and will be executed after the polling api response
   * then will publish to the subject subscriber with the agent queues detail data
   * @param response - agent queues detail api response object
   * @example -
   * ```
   * handleAgentQueuesDetailResponse(data);
   * ```
   */
  private handleAgentQueuesDetailResponse(response: any) {
    if (response) {
      const agentQueuesDetail: AgentQueuesDetail[] =
        this.formatAgentQueuesDetailResponse(response);
      if (agentQueuesDetail.length > 0) {
        this.cxoneClient.agentQueuesDetailSubject.next(agentQueuesDetail);
      }
    }
  }

  /**
   * Used to handle the agent queues detail api response and return the agent queues detail model object
   * @param response - the agent queues detail api response
   * @example -
   * ```
   * formatAgentQueuesDetailResponse(data);
   * ```
   * @returns - agent queue detail list
   */
  private formatAgentQueuesDetailResponse(data: { [key: string]: string | any }): AgentQueuesDetail[] {
    const agentQueuesDetailList = [] as Array<AgentQueuesDetail>;
    if (data?.resultSet?.contacts) {
      data.resultSet.contacts.forEach(
        (contact: { [key: string]: string }) => {
          const agentQueuesDetail =  new AgentQueuesDetail();
          agentQueuesDetail.parse(contact);
          agentQueuesDetailList.push(agentQueuesDetail);
        }
      );
    }
    return agentQueuesDetailList;
  }

  /**
   * Use to initializing the util worker and will return the method inside the worker
   * @example
   * ```
   * this.initAgentQueuesDetailWorker();
   * ```
   */
  public initAgentQueuesDetailWorker() {
    if (!this.pollingWorker)
      this.pollingWorker = new Worker(
        '../worker/cxone-agent-queues-detail-worker',
        {
          name: 'ccf-agent-queues-detail-polling-worker',
          type: 'module',
        }
      );
    return wrap<AgentQueuesDetailWorker>(this.pollingWorker);
  }

  /**
   * Use to terminate the agent queue worker
   * @example -
   * ```
   * this.terminatePolling
   * ```
   */
  public terminatePolling() {
    this.pollingWorker.terminate();
    this.pollingWorker = undefined;
  }
}
